"use client";

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AdminAuthClient } from "./client.js";
import type { AdminPermission, AdminPrincipal, AdminRole } from "./types.js";

export interface AdminAuthState {
  user: AdminPrincipal | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: AdminPermission) => boolean;
  hasRole: (role: AdminRole) => boolean;
  isCxUser: boolean;
  isPmUser: boolean;
  isAdminUser: boolean;
}

const AdminAuthContext = createContext<AdminAuthState | undefined>(undefined);

const ROLE_LEVELS: Record<AdminRole, number> = {
  viewer: 1,
  user: 2,
  cx_team_user: 2,
  cx_team_admin: 3,
  tech_staff: 3,
  partner_manager: 3,
  pm: 3,
  admin: 4,
  super_admin: 5,
};

function principalHasRole(user: Pick<AdminPrincipal, "role"> | null, required: AdminRole): boolean {
  if (!user?.role) return false;
  return (ROLE_LEVELS[user.role] ?? 0) >= (ROLE_LEVELS[required] ?? 0);
}

function principalHasPermission(
  user: Pick<AdminPrincipal, "permissions"> | null,
  permission: AdminPermission,
): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

function userTypeOf(user: AdminPrincipal | null): "admin" | "cx" | "pm" | null {
  if (!user) return null;
  if (user.role === "cx_team_admin" || user.role === "cx_team_user") return "cx";
  const appRole = user.appSpecificRole?.toLowerCase();
  if (appRole === "cx" || appRole === "cx_agent") return "cx";
  if (appRole === "pm" || appRole === "partner_manager") return "pm";
  if (user.role === "partner_manager" || user.role === "pm") return "pm";
  return "admin";
}

export interface AdminAuthProviderProps {
  /** Client returned by `createAdminAuthClient()`. */
  client: AdminAuthClient;
  /** Logger called when /api/auth/me fails. Defaults to a noop. */
  onLoadError?: (error: unknown) => void;
  children: ReactNode;
}

/**
 * Provides the Admin SSO principal to every descendant. Mirrors the mga-admin
 * `AuthProvider` shape so the lift is a small consumer-side rewrite.
 */
export function AdminAuthProvider({
  client,
  onLoadError,
  children,
}: AdminAuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<AdminPrincipal | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<AdminPrincipal | null>(null);

  const loadUser = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const current = await client.getCurrentUser();
      setUser(current);
    } catch (e) {
      onLoadError?.(e);
      setError("Failed to authenticate");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [client, onLoadError]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    loadUser();
    if (typeof document === "undefined") return;
    const onVisibility = (): void => {
      if (document.visibilityState === "visible" && userRef.current) {
        loadUser();
      }
    };
    const onFocus = (): void => {
      if (userRef.current) loadUser();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadUser]);

  const login = useCallback(
    (returnUrl?: string): void => {
      client.redirectToLogin(returnUrl);
    },
    [client],
  );

  const logout = useCallback(async (): Promise<void> => {
    await client.logout();
    setUser(null);
  }, [client]);

  const refreshUser = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await loadUser();
  }, [loadUser]);

  const hasPermission = useCallback(
    (permission: AdminPermission): boolean => principalHasPermission(user, permission),
    [user],
  );

  const hasRole = useCallback((role: AdminRole): boolean => principalHasRole(user, role), [user]);

  const value = useMemo<AdminAuthState>(() => {
    const userType = userTypeOf(user);
    return {
      user,
      isLoading,
      isAuthenticated: user !== null,
      error,
      login,
      logout,
      refreshUser,
      hasPermission,
      hasRole,
      isCxUser: userType === "cx",
      isPmUser: userType === "pm",
      isAdminUser: userType === "admin",
    };
  }, [error, hasPermission, hasRole, isLoading, login, logout, refreshUser, user]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

/**
 * Read the current admin auth state. Throws when called outside an
 * `<AdminAuthProvider>` ancestor — this matches the mga-admin contract and
 * surfaces wiring mistakes immediately.
 */
export function useAdminAuth(): AdminAuthState {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  return ctx;
}

/** Convenience: `useAdminAuth().hasPermission(permission)`. */
export function useAdminPermission(permission: AdminPermission): boolean {
  return useAdminAuth().hasPermission(permission);
}

/** Convenience: `useAdminAuth().hasRole(role)`. */
export function useAdminRole(role: AdminRole): boolean {
  return useAdminAuth().hasRole(role);
}

/** True if the principal has any of the supplied permissions. */
export function useAnyAdminPermission(permissions: readonly AdminPermission[]): boolean {
  const { user } = useAdminAuth();
  if (!user?.permissions) return false;
  return permissions.some((p) => user.permissions.includes(p));
}
